/**
 * THE phone ↔ uc-core sync contract. Everything the phone puts on the wire for POST /sync/commands
 * and PUT /media/{id} is defined in this file and nowhere else, so aligning with the backend's
 * `sync` module (task F4.1-g) is a one-file change.
 *
 * Source: docs/f4-f5/sections/F4.1.md → engineering.api (PROPOSED) and council R01.
 *  - every phone write is a named command with a UUIDv7 id made on the phone
 *  - commands are sent in the order they were made; the server applies each in its own transaction
 *  - same id + same payload → DUPLICATE (nothing changes); same id + different payload → 409/CONFLICT
 *  - a business-rule break never rejects a command: it is APPLIED or HELD with exception ids
 *  - WAIT_FOR_PARENT: a command it depends on has not arrived yet; the phone resends it later
 *  - 400 only for a malformed command, 401 only for a bad token
 *
 * ALIGNMENT NOTES (open until the backend sync module lands):
 *  - The spec draft names the command field `name`; this build sends `type` (see toWireCommand).
 *  - `session_id` and `capture_mode` are spec fields; `depends_on` is our addition so the server can
 *    answer WAIT_FOR_PARENT without guessing — the server may ignore it.
 *  - The response is read from the uc-core envelope's `data`.
 */
import { z } from 'zod'

export const SYNC_PROTOCOL_VERSION = 1
export const SYNC_COMMANDS_PATH = '/sync/commands'
export const mediaPath = (mediaId: string) => `/media/${encodeURIComponent(mediaId)}`

/** Max commands per POST. Small enough to keep a slow 2G request under the timeout. */
export const MAX_COMMANDS_PER_BATCH = 25

/** Every command name in the F4–F7 spec. Feature engineers add payload schemas in commandRegistry. */
export const COMMAND_TYPES = [
  // F4 farm gate
  'open_session',
  'confirm_roll',
  'commit_delivery',
  'void_delivery',
  'void_tag',
  'retag',
  'spoil_slip',
  'close_session',
  /** V1: a farmer's account number + IFSC typed in with no internet; the server runs the ₹1 check. */
  'add_bank_account',
  // F5 store
  'load_bags',
  'dispatch_shipment',
  'receive_shipment',
  'put_away',
  'move_bags',
  'count_bay',
  'mark_bags_conv',
  'sign_bay_declaration',
  'replace_container',
  // F6 process
  'bind_machine_label',
  'open_run',
  'cancel_run',
  'issue_to_run',
  'close_issue',
  'confirm_cleanout',
  'weigh_in',
  'report_mixing',
  'record_output',
  'void_output',
  'close_run',
  'seal_sample',
  'correct_run',
  'decide_run',
  // F7 pack & label
  'verify_pouch',
  'weigh_pouches',
  'pack_close',
] as const
export type CommandType = (typeof COMMAND_TYPES)[number]

/** APP for a normal day; PAPER_BACKFILL when typing in a paper-day slip (F4-B3). */
export type CaptureMode = 'APP' | 'PAPER_BACKFILL'

/** One command as sent on the wire. */
export interface WireCommand<P = unknown> {
  /** UUIDv7 made on the phone; the idempotency key. */
  id: string
  type: CommandType
  /** The unlocked PIN user (operator, or a supervisor on her phone). */
  actor_id: string
  /** ISO-8601 UTC instant the person did it (phone clock). */
  occurred_at: string
  session_id: string | null
  capture_mode: CaptureMode
  /** Ids of earlier commands this one needs applied first. */
  depends_on: string[]
  payload: P
}

/** Body of POST /sync/commands. */
export interface SyncCommandsRequest {
  /** Server device id from registration (falls back to the phone's device_uid before that). */
  device_id: string
  protocol_version: number
  /** ISO instant the batch left the phone — lets the server estimate clock skew. */
  sent_at: string
  commands: WireCommand[]
}

export const COMMAND_RESULTS = [
  'APPLIED',
  'DUPLICATE',
  'HELD',
  'WAIT_FOR_PARENT',
  'REJECTED_MALFORMED',
  'CONFLICT',
  /** Unexpected server failure: rolled back, nothing stored — resend later. */
  'ERROR',
] as const
export type CommandResultStatus = (typeof COMMAND_RESULTS)[number]

export const commandResultSchema = z.object({
  id: z.string(),
  result: z.enum(COMMAND_RESULTS),
  exception_ids: z.array(z.string()).optional().default([]),
  /** Whatever the handler wants the phone to cache (e.g. session status, unit ids). */
  server_state: z.unknown().optional(),
  error: z.object({ code: z.string().optional(), message: z.string() }).optional(),
})
export type CommandResult = z.infer<typeof commandResultSchema>

export const syncCommandsResponseSchema = z.object({
  results: z.array(commandResultSchema),
  /** Server clock, for clock_skew_s on open_session (F4.1). */
  server_time: z.string().optional(),
  /** Set when the office revoked this phone: wipe roster + PIN checks, keep sending the outbox. */
  device: z
    .object({
      status: z.enum(['ACTIVE', 'REVOKED']),
      wipe: z.boolean().optional(),
    })
    .optional(),
})
export type SyncCommandsResponse = z.infer<typeof syncCommandsResponseSchema>

/** Results after which the command is finished on the phone (server has it). */
export const TERMINAL_OK_RESULTS: ReadonlySet<CommandResultStatus> = new Set([
  'APPLIED',
  'DUPLICATE',
  'HELD',
])
/** Results that will never succeed by resending — a person must look. */
export const TERMINAL_ERROR_RESULTS: ReadonlySet<CommandResultStatus> = new Set([
  'REJECTED_MALFORMED',
  'CONFLICT',
])

/** Map a stored outbox record to the wire shape. The only place that knows wire field names. */
export function toWireCommand(rec: {
  id: string
  type: CommandType
  actor_id: string
  occurred_at: string
  session_id: string | null
  capture_mode: CaptureMode
  parent_ids: string[]
  payload: unknown
}): WireCommand {
  return {
    id: rec.id,
    type: rec.type,
    actor_id: rec.actor_id,
    occurred_at: rec.occurred_at,
    session_id: rec.session_id,
    capture_mode: rec.capture_mode,
    depends_on: rec.parent_ids,
    payload: rec.payload,
  }
}

/** Media upload: PUT /media/{id} with the raw JPEG body. Re-sending the same id is a no-op server side. */
export const MEDIA_CONTENT_TYPE = 'image/jpeg'
